import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Permit, PermitRaise } from '@cloudsign/shared';
import { X } from 'lucide-react';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Alert, Button, SelectField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { permitsKey, usePermitSites, usePermitTypes } from './api';

export interface RaisePermitDrawerProps {
  onClose: () => void;
  /** Called with the new permit (status `requested`). */
  onRaised?: (permit: Permit) => void;
  /** Raised from a visit on the today board or a pre-registration: fixes the site and links the permit. */
  siteId?: string;
  visitId?: string | null;
  preRegistrationId?: string | null;
  memberId?: string | null;
  holderName?: string;
  holderCompany?: string;
}

/** Raise a permit to work. Exported so the today board and pre-registration pages (W23) can open it for one person. */
export function RaisePermitDrawer({ onClose, onRaised, siteId, visitId = null, preRegistrationId = null, memberId = null, holderName = '', holderCompany = '' }: RaisePermitDrawerProps) {
  const types = usePermitTypes();
  const sites = usePermitSites();
  const qc = useQueryClient();
  const toast = useToast();
  const [typeId, setTypeId] = useState('');
  const [site, setSite] = useState(siteId ?? '');
  const [name, setName] = useState(holderName);
  const [company, setCompany] = useState(holderCompany);
  const [area, setArea] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const activeTypes = (types.data ?? []).filter((x) => x.active);
  const chosenType = typeId || activeTypes[0]?.id || '';
  const chosenSite = site || sites.data?.[0]?.id || '';

  const raise = useMutation({
    mutationFn: () => {
      const body = PermitRaise.parse({ typeId: chosenType, siteId: chosenSite, visitId, preRegistrationId, memberId, holderName: name, holderCompany: company, area, description });
      return api.post('/permits', { body, schema: Permit });
    },
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: permitsKey });
      toast(t('p3pt.raise.done', { number: p.number }));
      onRaised?.(p);
      onClose();
    },
  });
  const errs = fieldErrors(raise.error);
  const needsHolder = memberId === null && name.trim() === '';
  const valid = chosenType !== '' && chosenSite !== '' && !needsHolder;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/40">
      <aside role="dialog" aria-modal="true" aria-label={t('p3pt.raise.title')} className="h-full w-full max-w-lg overflow-y-auto bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold">{t('p3pt.raise.title')}</h2>
          <button type="button" aria-label={t('common.close')} onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-slate-100">
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
        {types.isPending || sites.isPending ? (
          <Spinner />
        ) : activeTypes.length === 0 ? (
          <Alert kind="info">{t('p3pt.raise.noTypes')}</Alert>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (valid) raise.mutate();
            }}
          >
            <SelectField label={t('p3pt.field.type')} value={chosenType} error={errs['typeId']} onChange={(e) => setTypeId(e.target.value)}>
              {activeTypes.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </SelectField>
            <SelectField label={t('p3pt.field.site')} value={chosenSite} disabled={siteId !== undefined} error={errs['siteId']} onChange={(e) => setSite(e.target.value)}>
              {(sites.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </SelectField>
            <TextField label={t('p3pt.field.holder')} value={name} error={errs['holderName']} onChange={(e) => setName(e.target.value)} />
            <TextField label={t('p3pt.field.company')} value={company} onChange={(e) => setCompany(e.target.value)} />
            <TextField label={t('p3pt.field.area')} value={area} onChange={(e) => setArea(e.target.value)} />
            <div>
              <label htmlFor="p3pt-raise-description" className="mb-1 block text-sm font-medium text-slate-800">
                {t('p3pt.field.description')}
              </label>
              <textarea
                id="p3pt-raise-description"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm"
              />
            </div>
            <ErrorLine error={raise.error} />
            <div className="flex justify-end gap-2">
              <Button onClick={onClose}>{t('common.cancel')}</Button>
              <Button type="submit" variant="primary" disabled={!valid || raise.isPending}>
                {t('p3pt.raise.submit')}
              </Button>
            </div>
          </form>
        )}
      </aside>
    </div>
  );
}
